import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import format from 'string-template';

import defaultMealImage from '../../../../../assets/defaultMealImage.svg';
import { ReactComponent as ViewIcon } from '../../../../../assets/icons/v2/arrow-right.svg';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import LoadingPage from '../../../../../components/LoadingPage';
import RecipePreference, { RecipePreferenceType } from '../../../../Model/RecipePreference';
import Recipe from '../../../../Model/Recipe';
import { MealPortal } from '../../../MealPlanPortal';

import {
  Container,
  RecipeRow,
  RecipeImage,
  RecipeName,
  ButtonContainer,
  StyledAccordion,
  ReasonTagsContainer,
  StyledTag,
  StyledModal,
  ModalContentWrapper,
} from './styles';

import texts from './texts.json';

const MAX_VISIBLE_REASONS = 3;

const RecipePreferences = ({ user }) => {
  const [recipePreferences, setRecipePreferences] = useState({ hasDocs: false, docs: [] });
  const [recipeMap, setRecipeMap] = useState({});

  const [openPortalModal, setOpenPortalModal] = useState(false);
  const [portalOptions, setPortalOptions] = useState({});

  const [isInit, setIsInit] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();

  // Fetch recipe preferences when component is mounted, set isInit to true when done
  useEffect(() => {
    const init = async () => {
      const recipePreferencesCollection = await RecipePreference.getRecipePreferences(user);
      if (isComponentMountedRef.current) {
        setRecipePreferences(recipePreferencesCollection);
        setIsInit(true);
      }
    };
    if (!isInit) {
      init();
    }
  }, [user, isComponentMountedRef, isInit]);

  // Fetch recipes when recipe preferences are loaded, set isReady to true when done
  useEffect(() => {
    if (recipePreferences.hasDocs) {
      const fetchRecipes = async () => {
        const recipeDocs = [];
        const resultMap = {};

        recipePreferences.docs.forEach((recipePreferenceDoc) => {
          recipeDocs.push(new Recipe(recipePreferenceDoc.recipeId));
        });

        if (recipeDocs.length > 0) {
          // Fetch recipe docs
          await Promise.all(recipeDocs.map((recipeDoc) => recipeDoc.init()));
          recipeDocs.forEach((recipeDoc) => {
            // Build map of recipe id to recipe doc
            resultMap[recipeDoc.id] = recipeDoc;
          });
        }

        setRecipeMap(resultMap);
        setIsReady(true);
      };
      fetchRecipes();
    } else if (isInit) {
      // Set isReady to true if there are no recipe preference documents
      setIsReady(true);
    }
  }, [recipePreferences, isInit]);

  // Group recipe preferences by likes and dislikes
  const [likedRecipePreferences, dislikedRecipePreferences] = useMemo(() => {
    const liked = [];
    const disliked = [];
    recipePreferences.docs.forEach((recipePreferenceDoc) => {
      if (recipePreferenceDoc.preference === RecipePreferenceType.LIKE) {
        liked.push(recipePreferenceDoc);
      } else {
        disliked.push(recipePreferenceDoc);
      }
    });
    return [liked, disliked];
  }, [recipePreferences]);

  const handleViewRecipe = (recipeId) => {
    setPortalOptions({ recipeId });
    setOpenPortalModal(true);
  };

  const handlePortalClose = () => {
    setOpenPortalModal(false);
    setPortalOptions({});
  };

  // Helper function to render dislike reason tags with a tooltip
  const renderDislikeReasonTags = (recipePreferenceDoc) => {
    const visibleReasons = recipePreferenceDoc.reason.slice(0, MAX_VISIBLE_REASONS);
    const remainingReasons = recipePreferenceDoc.reason.slice(MAX_VISIBLE_REASONS);
    const tooltipText = remainingReasons.join(', ');

    return (
      <ReasonTagsContainer>
        {visibleReasons.map((reasonText) => (
          <StyledTag key={`${recipePreferenceDoc.recipeId}-${reasonText}`}>{reasonText}</StyledTag>
        ))}
        {remainingReasons.length > 0 && (
          <Tooltip title={tooltipText} placement="top" arrow>
            <StyledTag>
              {format(texts.moreReasons, { amount: remainingReasons.length })}
            </StyledTag>
          </Tooltip>
        )}
      </ReasonTagsContainer>
    );
  };

  // Render a recipe preference row
  const renderRecipe = (recipePreferenceDoc) => {
    const recipe = recipeMap[recipePreferenceDoc.recipeId];
    if (!recipe) {
      return null;
    }

    const showDislikeReason = recipePreferenceDoc.preference === RecipePreferenceType.DISLIKE
      && recipePreferenceDoc.reason
      && recipePreferenceDoc.reason.length > 0;

    return (
      <RecipeRow key={recipePreferenceDoc.recipeId}>
        <RecipeImage src={recipe.image || defaultMealImage} />
        <RecipeName>
          {recipe.name}
          {showDislikeReason && renderDislikeReasonTags(recipePreferenceDoc)}
        </RecipeName>
        <ButtonContainer>
          <PrimaryButton
            onClick={() => handleViewRecipe(recipePreferenceDoc.recipeId)}
            endIcon={<ViewIcon />}
            variant="info"
            size="medium"
          >
            {texts.view}
          </PrimaryButton>
        </ButtonContainer>
      </RecipeRow>
    );
  };

  if (!isReady) {
    return <LoadingPage />;
  }

  return (
    <Container>
      {recipePreferences.hasDocs ? (
        <>
          {likedRecipePreferences.length > 0 && (
            <StyledAccordion
              title={texts.likes}
              defaultExpanded
            >
              {likedRecipePreferences.map((preferenceDoc) => renderRecipe(preferenceDoc))}
            </StyledAccordion>
          )}
          {dislikedRecipePreferences.length > 0 && (
            <StyledAccordion
              title={texts.dislikes}
              defaultExpanded
            >
              {dislikedRecipePreferences.map((preferenceDoc) => renderRecipe(preferenceDoc))}
            </StyledAccordion>
          )}
          <StyledModal
            open={openPortalModal}
            onClose={handlePortalClose}
          >
            <ModalContentWrapper>
              <MealPortal
                options={portalOptions}
              />
            </ModalContentWrapper>
          </StyledModal>
        </>
      ) : (
        texts.emptyText
      )}
    </Container>
  );
};

RecipePreferences.propTypes = {
  user: PropTypes.string.isRequired,
};

export default RecipePreferences;
